import React, {Component, useState} from "react";
import {
    Grid,
    LinearProgress,
    Select,
    OutlinedInput,
    MenuItem,
    Button, 
    CircularProgress,
    Card,
    CardContent,
    CardActions,
    CardMedia,
    Paper,
    Box,
    Divider,
    IconButton
} from "@material-ui/core";
import {
    Satellite as SatelliteIcon,
    Terrain as LandscapeIcon,
    Cloud as CloudIcon,
    Timeline as TimelineIcon,
    NavigateNext as NavigateNextIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    BarChart as BarChartIcon
} from '@material-ui/icons';
import {useTheme} from "@material-ui/styles";
import {
    ResponsiveContainer,
    ComposedChart,
    AreaChart,
    LineChart,
    Line,
    Area,
    PieChart,
    Pie,
    Cell,
    YAxis,
    XAxis, 
    Bar, 
    BarChart,
    Tooltip, 
    Legend, 
    CartesianGrid
} from "recharts";

import axios from "axios";
import {server_base_url} from "../../context/UserContext";
import {ShimmerTable} from "react-shimmer-effects";
// styles
import useStyles from "./styles";

// components
import mock from "./mock";
import Widget from "../../components/Widget";
import PageTitle from "../../components/PageTitle";
import {Typography} from "../../components/Wrappers";
import Dot from "../../components/Sidebar/components/Dot";
import Table from "./components/Table/Table";
import BigStat from "./components/BigStat/BigStat";
import APIKeyCreation from "./APIKeyCreation";

import OnboardComponent from "./OnboardComponent";

const API_URL = server_base_url + "/api/";

// Empty state component for widgets that will show no data available message
const EmptyStateWidget = ({ message = "No data available" }) => (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={3}>
        <WarningIcon style={{ fontSize: 40, color: '#ccc', marginBottom: 16 }} />
        <Typography variant="body1" color="textSecondary">{message}</Typography>
        <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
            Data will appear here once connected to API
        </Typography>
    </Box>
);

// Metric card component for dashboard metrics
const MetricCard = ({ icon, title, value, tooltip, status, color }) => {
    const getStatusIcon = () => {
        if (status === 'success') return <CheckCircleIcon style={{ color: '#4caf50' }} />;
        if (status === 'warning') return <WarningIcon style={{ color: '#ff9800' }} />;
        return null;
    };
    
    return (
        <Card>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box display="flex" alignItems="center">
                        {icon}
                        <Typography variant="subtitle1" style={{ marginLeft: 8 }}>{title}</Typography>
                    </Box>
                    {getStatusIcon()}
                </Box>
                <Typography variant="h4" component="div" style={{ marginBottom: '12px' }}>{value || "--"}</Typography>
                {tooltip && (
                    <Typography variant="caption" color="textSecondary" component="p" style={{ fontSize: '11px', lineHeight: 1.3 }}>
                        {tooltip}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

// Navigation card component to link to other sections
const NavigationCard = ({ title, description, icon, linkTo, bgImage, primary }) => {
    const cardStyle = primary ? {
        backgroundImage: bgImage ? `url(${bgImage})` : 'linear-gradient(45deg, #2196F3, #21CBF3)',
        color: '#fff',
        minHeight: 180
    } : {};
    
    const textColor = primary ? '#fff' : 'inherit';
    
    return (
        <Card style={cardStyle}>
            <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                    {icon}
                    <Typography variant="h5" style={{ marginLeft: 8, color: textColor }}>{title}</Typography>
                </Box>
                <Typography variant="body2" style={{ marginBottom: 16, color: textColor }}>
                    {description}
                </Typography>
            </CardContent>
            <CardActions>
                <Button 
                    color={primary ? "inherit" : "primary"}
                    endIcon={<NavigateNextIcon />}
                    href={linkTo}
                >
                    Explore
                </Button>
            </CardActions>
        </Card>
    );
};

class BusinessDashboard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            home: true,
            profile: false,
            name: '',
            email: '',
            errors: {},
            my_api_keys: [],
            my_connected_apps: [],
            content: {},
            users: [],
            api_data: null,
            usersLength: 0,
            message: "",
            datatableData: null,
            onboard: localStorage.getItem('verified_status'),
            // Dashboard metrics
            dashboardMetrics: {
                monitoredLandArea: { value: null, unit: 'hectares', count: 0, status: 'warning' },
                healthIndex: { value: null, status: 'warning', trend: 'stable', samples: 0 },
                latestSatelliteUpdate: { date: null, days_ago: null, status: 'warning', image_count: 0 }
            },
            isLoading: true
        };
    }

    async componentDidMount() {
        const self = this;
        try {
            // Fetch insights data from legacy API
            const insightsPromise = axios({
                method: "get",
                url: API_URL + 'get_insights/?email='+ localStorage.getItem('id_token'),
                headers: {"Content-Type": "multipart/form-data"},
            });
            
            // Fetch dashboard metrics from new API
            const apiService = (await import('../../services/api')).default;
            const dashboardMetricsPromise = apiService.getDashboardMetrics();
            
            // Wait for both requests to complete
            const [insightsResponse, dashboardMetrics] = await Promise.all([insightsPromise, dashboardMetricsPromise]);
            
            // Process insights data
            let sortedResults = [];
            if (insightsResponse.data && insightsResponse.data.length > 0) {
                const parsedJSON = insightsResponse.data;
                for (let i = 0; i < parsedJSON.length; i++) {
                    sortedResults = parsedJSON[i].graph_data;
                }
            }
            
            // Update state with all data
            this.setState({
                api_data: sortedResults,
                dashboardMetrics: dashboardMetrics,
                isLoading: false
            });
            
            console.log('Dashboard metrics loaded:', dashboardMetrics);
            
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            this.setState({ isLoading: false });
        }
    }



    render() {
        var classes = useStyles;
        var theme = useTheme;
        
        // EO Analysis focused dashboard
        const dashboardContent = (
            <>
                <PageTitle title="Earth Observation Dashboard" />
                
                {/* Earth Observation Highlights Section */}
                <Grid container spacing={3} style={{ marginBottom: 24 }}>
                    <Grid item xs={12} md={4}>
                        <MetricCard 
                            icon={<SatelliteIcon style={{ color: '#2196F3' }} />}
                            title="Monitored Land Area"
                            value={this.state.dashboardMetrics.monitoredLandArea.value ? 
                                `${this.state.dashboardMetrics.monitoredLandArea.value} ${this.state.dashboardMetrics.monitoredLandArea.unit}` : "--"}
                            tooltip={`Total area being monitored under your account. ${this.state.dashboardMetrics.monitoredLandArea.count} fields registered.`}
                            status={this.state.dashboardMetrics.monitoredLandArea.status}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <MetricCard 
                            icon={<TimelineIcon style={{ color: '#4CAF50' }} />}
                            title="EO Health Index"
                            value={this.state.dashboardMetrics.healthIndex.value ? 
                                this.state.dashboardMetrics.healthIndex.value.toFixed(2) : "--"}
                            tooltip={`Aggregated vegetation health index from ${this.state.dashboardMetrics.healthIndex.samples} satellite imagery samples.`}
                            status={this.state.dashboardMetrics.healthIndex.status}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <MetricCard 
                            icon={<CloudIcon style={{ color: '#FF9800' }} />}
                            title="Latest Satellite Update"
                            value={this.state.dashboardMetrics.latestSatelliteUpdate.date || "--"}
                            tooltip={this.state.dashboardMetrics.latestSatelliteUpdate.days_ago ? 
                                `Imagery captured ${this.state.dashboardMetrics.latestSatelliteUpdate.days_ago} days ago (${this.state.dashboardMetrics.latestSatelliteUpdate.image_count} images available).` :
                                "No recent satellite imagery available."}
                            status={this.state.dashboardMetrics.latestSatelliteUpdate.status}
                        />
                    </Grid>
                </Grid>
                
                {/* Quick Action Navigation Cards */}
                <Typography variant="h5" style={{ marginBottom: 16 }}>
                    Quick Navigation
                </Typography>
                <Grid container spacing={3} style={{ marginBottom: 24 }}>
                    <Grid item xs={12} md={6}>
                        <NavigationCard 
                            title="EO Analysis"
                            description="Explore detailed satellite analysis for your lands with NDVI tracking and crop health monitoring"
                            icon={<SatelliteIcon style={{ color: '#fff' }} />}
                            linkTo="/#/app/eo-analysis-farmer"
                            primary={true}
                        />
                    </Grid>
                    {/* <Grid item xs={12} md={3}>
                        <NavigationCard 
                            title="Field Management"
                            description="Manage your registered fields and boundaries"
                            icon={<LandscapeIcon />}
                            linkTo="/#/app/eo-analysis-farmer"
                        />
                    </Grid> */}
                    <Grid item xs={12} md={6}>
                        <NavigationCard 
                            title="Market Prices"
                            description="View agricultural market prices from Joburg Market"
                            icon={<BarChartIcon />}
                            linkTo="/#/app/market-prices"
                        />
                    </Grid>
                </Grid>
                
                {/* Featured Insights Cards */}
                {/* <Typography variant="h5" style={{ marginBottom: 16 }}>
                    Featured Insights
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Widget
                            header={
                                <Typography variant="h6">Crop Health Trends</Typography>
                            }
                            bodyClass={classes.mainChartBody}
                        >
                            <EmptyStateWidget message="No crop health data available" />
                        </Widget>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Widget
                            header={
                                <Typography variant="h6">Climate Risk Indicators</Typography>
                            }
                        >
                            <EmptyStateWidget message="No climate data available" />
                        </Widget>
                    </Grid>
                </Grid> */}
                
                {/* Informational Resources */}
                {/* <Typography variant="h5" style={{ marginTop: 32, marginBottom: 16 }}>
                    Resources & Updates
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Paper style={{ padding: 16 }}>
                            <Typography variant="h6" style={{ marginBottom: 16 }}>
                                Latest Platform Updates
                            </Typography>
                            <EmptyStateWidget message="No updates available" />
                        </Paper>
                    </Grid>
                </Grid> */}
            </>
        );

        return (
            <>
                {this.state.onboard && this.state.onboard === "False" ? (
                    <OnboardComponent/>
                ) : (
                    <>{dashboardContent}</>
                )}
            </>
        );

    }
}


export default BusinessDashboard;
